import { Box, Button, Card, styled, useTheme, Tabs, Tab, Typography, FormControl, InputLabel, Select, MenuItem, Grid, Chip } from "@mui/material";
import AppPagination from "components/AppPagination";
import { Search, Refresh } from "@mui/icons-material";
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import { useEffect, useState, useRef } from "react";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { usePageTitle } from "contexts/PageTitleContext";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import ReturnCancelRequestTable from "./ReturnCancelRequestTable";
import AdvancedExportModal from "pages/Products/AdvancedExportModal";
import { alpha } from "@mui/material/styles";
import ConfirmationDialog from "components/ConfirmationDialog";
import buttonConfig from "config/ConfigAccessButton";


const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  minHeight: 42,
  width: "100%",
  "& .MuiTabs-indicator": {
    display: "none",
  },
  "& .MuiTabs-flexContainer": {
    gap: 4,
    padding: 4,
  },
  "& .MuiTabs-scroller": {
    [theme.breakpoints.up("md")]: {
      "& .MuiTabs-flexContainer": {
        justifyContent: "space-between",
      },
    },
  },
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 2px 8px rgba(0,0,0,0.3)"
      : "0 2px 8px rgba(0,0,0,0.1)",
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  minHeight: 36,
  borderRadius: theme.shape.borderRadius,
  margin: 0,
  padding: "6px 12px",
  color: theme.palette.text.secondary,
  transition: "all 0.2s ease-in-out",
  position: "relative",
  [theme.breakpoints.down("md")]: {
    flex: 1,
    minWidth: 0,
    padding: "8px 12px",
    fontSize: "0.8rem",
  },
  [theme.breakpoints.up("md")]: {
    flex: 1,
    minWidth: 0,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
  },
  "&.Mui-selected": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const getStatusColor = (theme, status) => {
  const colors = {
    cancelled: theme.palette.error.main,
    returned: theme.palette.error.dark,
  };
  return colors[status] || theme.palette.grey[500];
};

const TabContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  position: "sticky",
  top: 0,
  zIndex: 10,
  backgroundColor: theme.palette.background.default,
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  [theme.breakpoints.down("md")]: {
    marginBottom: theme.spacing(1),
  },
}));

const FilterCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  background: theme.palette.background.paper,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
}));

const FilterHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.25, 1.5),
  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  background: alpha(theme.palette.primary.main, 0.02),
  borderRadius: `${theme.spacing(1.5)} ${theme.spacing(1.5)} 0 0`,
}));

const TABS = [
  { value: "Cancelled", label: "Cancel Request", category: "cancelled" },
  { value: "Returned", label: "Return Request", category: "returned" },
];

const pageSizeOptions = [10, 25, 50, 100];

/** Cancel/return list APIs may return rows as a top-level array or nested (search responses often differ). */
function extractRequestRowsFromResponse(response) {
  if (!response || typeof response !== "object") return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.results)) return response.results;
  if (Array.isArray(response.data)) return response.data;

  const dataObj = response.data;
  if (dataObj && typeof dataObj === "object" && !Array.isArray(dataObj)) {
    if (Array.isArray(dataObj.results)) return dataObj.results;
    if (Array.isArray(dataObj.data)) return dataObj.data;
    const keys = [
      "orders",
      "cancel_requests",
      "return_requests",
      "requests",
      "Order",
      "CancelRequest",
      "ReturnRequest",
    ];
    for (const k of keys) {
      if (Array.isArray(dataObj[k])) return dataObj[k];
    }
    const firstArray = Object.values(dataObj).find((v) => Array.isArray(v));
    if (firstArray) return firstArray;
  }
  return [];
}

function extractPaginationFromResponse(response) {
  if (!response || typeof response !== "object") return null;
  return (
    response.pagination ||
    response.meta ||
    (response.data &&
      typeof response.data === "object" &&
      !Array.isArray(response.data) &&
      response.data.pagination) ||
    null
  );
}

const ReturnCancelRequest = () => {
  const theme = useTheme();
  const { setPageTitle } = usePageTitle();
  const toast = useToast();
  const isCompanyUser = apiService.isCompanyUser();
  const companyId = apiService.getCompanyId();
  const companyDetails = apiService.getCompanyDetails();

  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(isCompanyUser ? companyId : "all");
  const [statusFilter, setStatusFilter] = useState("Cancelled");
  const [searchValue, setSearchValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [advancedExportModal, setAdvancedExportModal] = useState(false);
  const [counts, setCounts] = useState({ Cancelled: 0, Returned: 0 });
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [updatingReturnOrderId, setUpdatingReturnOrderId] = useState(null);
  const [checkingPaymentOrderId, setCheckingPaymentOrderId] = useState(null);
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    confirmButtonText: "Confirm",
    confirmButtonColor: "primary",
  });
  
  // State for dropdown filters - Separate for Cancel and Return
  const [paymentStatusOptions, setPaymentStatusOptions] = useState([]);
  const [orderStatusOptions, setOrderStatusOptions] = useState([]);
  
  // Request Status options
  const requestStatusOptions = [
    { id: "pending", name: "Pending" },
    { id: "approved", name: "Approved" },
    { id: "rejected", name: "Rejected" }
  ];
  
  // Cancel Request filters
  const [cancelSelectedPaymentStatus, setCancelSelectedPaymentStatus] = useState("");
  const [cancelSelectedOrderStatus, setCancelSelectedOrderStatus] = useState("");
  const [cancelSelectedRequestStatus, setCancelSelectedRequestStatus] = useState("");
  
  // Return Request filters
  const [returnSelectedPaymentStatus, setReturnSelectedPaymentStatus] = useState("");
  const [returnSelectedOrderStatus, setReturnSelectedOrderStatus] = useState("");
  const [returnSelectedRequestStatus, setReturnSelectedRequestStatus] = useState("");

  // ============ PERMISSION FUNCTIONS ============
  // Get current tab name based on status filter
  const getCurrentTabName = () => {
    const tabMapping = {
      'Cancelled': 'cancel request button',
      'Returned': 'return request button'
    };
    return tabMapping[statusFilter] || 'cancel request button';
  };
  
  // Get button permissions based on current tab
  const getCurrentButtonPermissions = () => {
    const currentTab = getCurrentTabName();
    const moduleConfig = buttonConfig["Return & Cancel Request"];
    return moduleConfig && moduleConfig[currentTab] ? moduleConfig[currentTab] : {};
  };
  
  // Check if a specific button should be enabled
  const shouldEnableButton = (buttonKey) => {
    const permissions = getCurrentButtonPermissions();
    return permissions[buttonKey] === true;
  };
  // ============ END PERMISSION FUNCTIONS ============

  const isInitialMount = useRef(true);
  const loadedTabs = useRef(new Set());

  useEffect(() => {
    setPageTitle("Return & Cancel Request");
    fetchCompanies();
    fetchFilterOptions();
    // Initial load: only the active request-type list API (counts come from the same response)
    fetchProductsForSelectedTab();
  }, [setPageTitle]);

  const fetchCompanies = async () => {
    try {
      if (isCompanyUser && companyDetails) {
        setCompanies([companyDetails]);
        return;
      }

      const res = await apiService.get(API_ENDPOINTS.GET_company);
      if (res?.success) {
        setCompanies(res.data?.Company || []);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const userId = localStorage.getItem("user_id") || "1";
      
      // Fetch payment status options
      const paymentResponse = await apiService.get(API_ENDPOINTS.GET_payment_status_choices);
      if (paymentResponse?.success && paymentResponse?.data) {
        setPaymentStatusOptions(paymentResponse.data);
      }
      
      // Fetch order status options with user_id parameter
      const orderResponse = await apiService.get(`${API_ENDPOINTS.GET_order_status_choices_with_count}?user_id=${userId}`);
      if (orderResponse?.success && orderResponse?.data?.statuses) {
        setOrderStatusOptions(orderResponse.data.statuses);
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  // Fetch products for the currently selected tab only
  const fetchProductsForSelectedTab = async () => {
    setIsLoading(true);
    const userId = localStorage.getItem("user_id");

    try {
      // When searching, fetch all results across all pages
      // Otherwise, use normal pagination
      const isSearchActive = searchTerm && searchTerm.trim() !== "";
      const fetchPageSize = isSearchActive ? 10000 : pageSize; // Large page size for search to get all results
      const fetchPage = isSearchActive ? 1 : currentPage; // Always fetch page 1 for search

      // Build query parameters for filters based on selected tab
      const params = new URLSearchParams();
      params.append("user_id", userId);
      params.append("page", fetchPage);
      params.append("page_size", fetchPageSize);
      if (selectedCompany && selectedCompany !== "all") {
        params.append("company", selectedCompany);
      }
      
      // Use filters based on which tab is active
      if (statusFilter === "Cancelled") {
        // Cancel Request filters
        if (cancelSelectedPaymentStatus) {
          params.append("payment_status", cancelSelectedPaymentStatus);
        }
        if (cancelSelectedOrderStatus) {
          params.append("order_status", cancelSelectedOrderStatus);
        }
        if (cancelSelectedRequestStatus) {
          params.append("request_status", cancelSelectedRequestStatus);
        }
      } else if (statusFilter === "Returned") {
        // Return Request filters
        if (returnSelectedPaymentStatus) {
          params.append("payment_status", returnSelectedPaymentStatus);
        }
        if (returnSelectedOrderStatus) {
          params.append("order_status", returnSelectedOrderStatus);
        }
        if (returnSelectedRequestStatus) {
          params.append("request_status", returnSelectedRequestStatus);
        }
      }
      
      if (searchTerm) {
        params.append("search", searchTerm);
      }
      
      // Only fetch the active tab data
      const activeEndpoint = statusFilter === "Cancelled" 
        ? API_ENDPOINTS.Get_cancel_request 
        : API_ENDPOINTS.Get_return_request;
      
      const activeResponse = await apiService.get(`${activeEndpoint}?${params.toString()}`);

      const raw = extractRequestRowsFromResponse(activeResponse);

      const list = raw.map((item) => ({
        ...(item.order || item),
        id: item?.order?.id ?? item?.order_id ?? item?.id,
      }));

      const pagination = extractPaginationFromResponse(activeResponse);

      setProducts(list);
      setTotalItems(pagination?.total_items ?? list.length);
      setTotalPages(pagination?.total_pages ?? 1);

      // Update count for current tab
      const currentCount = pagination?.total_items ?? list.length;
      if (statusFilter === "Cancelled") {
        setCounts(prev => ({ ...prev, Cancelled: currentCount }));
      } else {
        setCounts(prev => ({ ...prev, Returned: currentCount }));
      }

    } catch (error) {
      console.error("Error fetching data:", error);
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch products when dependencies change (only the selected tab)
  useEffect(() => {
    // Skip first run: initial data is loaded in the mount effect above
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    fetchProductsForSelectedTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, statusFilter, selectedCompany, cancelSelectedPaymentStatus, cancelSelectedOrderStatus, cancelSelectedRequestStatus, returnSelectedPaymentStatus, returnSelectedOrderStatus, returnSelectedRequestStatus, searchTerm]);

  const handlePageChange = (e, value) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    // Reset loaded tabs to force refetch with new page size
    loadedTabs.current.clear();
  };

  const handleCompanyChange = (e) => {
    setSelectedCompany(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (event, newValue) => {
    setCurrentPage(1);
    setStatusFilter(newValue);
    // Reset search state when changing tabs
    setSearchValue("");
    setSearchTerm("");
    setHasSearched(false);
  };

  const handleRefresh = async () => {
    await fetchProductsForSelectedTab();
  };

  useEffect(() => {
    setSelectedProducts([]);
    setIsAllSelected(false);
  }, [statusFilter]);

  const handleProductSelect = (productId, isSelected) => {
    setSelectedProducts((prev) => {
      if (isSelected) {
        const newSelected = [...prev, productId];
        setIsAllSelected(newSelected.length === products.length);
        return newSelected;
      }
      const newSelected = prev.filter((id) => id !== productId);
      setIsAllSelected(false);
      return newSelected;
    });
  };

  const handleSelectAll = (selectAll) => {
    if (selectAll) {
      setSelectedProducts(products.map((p) => p.id));
      setIsAllSelected(true);
    } else {
      setSelectedProducts([]);
      setIsAllSelected(false);
    }
  };

  const handleExport = async (format) => {
    const selectedData = products.filter((p) => selectedProducts.includes(p.id));
    if (selectedData.length === 0) {
      alert("Please select orders to export");
      return;
    }
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const filename = `return_cancel_export_${timestamp}`;
    if (format === "excel") {
      try {
        const XLSX = await import("xlsx");
        const excelData = selectedData.map((p) => ({
          "Order ID": p.order_id || "",
          "User Name": p.user?.username || p.user_name || "",
          "User Email": p.user?.email || p.user_email || "",
          Status: p.status?.name || p.order_status_display || "",
          "Payment Status": p.payment_status?.name || p.payment_status_display || "",
          "Total Amount": p.total_amount || "",
        }));
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");
        XLSX.writeFile(workbook, `${filename}.xlsx`);
      } catch (e) {
        console.error(e);
        alert("Export failed.");
      }
    } else if (format === "csv") {
      const headers = ["Order ID", "User Name", "User Email", "Status", "Payment Status", "Total Amount"];
      const rows = selectedData.map((p) =>
        [
          `"${p.order_id || ""}"`,
          `"${p.user?.username || p.user_name || ""}"`,
          `"${p.user?.email || p.user_email || ""}"`,
          `"${p.status?.name || p.order_status_display || ""}"`,
          `"${p.payment_status?.name || p.payment_status_display || ""}"`,
          `"${p.total_amount || ""}"`,
        ].join(",")
      );
      const csv = [headers.join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    }
  };

  const handleCancelRequestAction = async (orderId, action) => {
    if (!orderId) return;
    setUpdatingOrderId(orderId);
    try {
      const response = await apiService.post(API_ENDPOINTS.Update_cancel_request, {
        order_id: orderId,
        action: action === "approve" ? "approve" : "reject",
      });
      if (response?.success) {
        const actionValue = action === "approve" ? "approved" : "rejected";
        setProducts((prev) =>
          prev.map((p) =>
            (p.order_id ?? p.id) === orderId
              ? { ...p, cancel_request_action: actionValue }
              : p
          )
        );
        await fetchProductsForSelectedTab();
      } else {
        alert(response?.message || "Action failed.");
      }
    } catch (error) {
      console.error("Update cancel request error:", error);
      alert(error?.message || "Action failed.");
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handleReturnRequestAction = async (orderId, action) => {
    if (!orderId) return;
    setUpdatingReturnOrderId(orderId);
    try {
      const response = await apiService.post(API_ENDPOINTS.Update_return_request, {
        order_id: orderId,
        action: action === "approve" ? "approve" : "reject",
      });
      if (response?.success) {
        const actionValue = action === "approve" ? "approved" : "rejected";
        setProducts((prev) =>
          prev.map((p) =>
            (p.order_id ?? p.id) === orderId
              ? { ...p, return_request_action: actionValue }
              : p
          )
        );
        await fetchProductsForSelectedTab();
      } else {
        alert(response?.message || "Action failed.");
      }
    } catch (error) {
      console.error("Update return request error:", error);
      alert(error?.message || "Action failed.");
    } finally {
      setUpdatingReturnOrderId(null);
    }
  };

  const showConfirmation = (order, action, type) => {
    const isApprove = action === "approve";
    const isCancel = type === "cancel";
    const orderId = order?.order_id ?? order?.id ?? "this order";
    
    setConfirmationDialog({
      open: true,
      title: isApprove 
        ? `Accept ${isCancel ? "Cancel" : "Return"} Request`
        : `Reject ${isCancel ? "Cancel" : "Return"} Request`,
      message: isApprove
        ? `Are you sure you want to accept the ${isCancel ? "cancel" : "return"} request for order ${orderId}?`
        : `Are you sure you want to reject the ${isCancel ? "cancel" : "return"} request for order ${orderId}?`,
      confirmButtonText: isApprove ? "Accept" : "Reject",
      confirmButtonColor: isApprove ? "success" : "error",
      onConfirm: async () => {
        setConfirmationDialog((prev) => ({ ...prev, open: false }));
        if (isCancel) {
          await handleCancelRequestAction(orderId, action);
        } else {
          await handleReturnRequestAction(orderId, action);
        }
      },
    });
  };

  const handleApproveCancelRequest = (order) => {
    showConfirmation(order, "approve", "cancel");
  };

  const handleRejectCancelRequest = (order) => {
    showConfirmation(order, "reject", "cancel");
  };

  const handleApproveReturnRequest = (order) => {
    showConfirmation(order, "approve", "return");
  };

  const handleRejectReturnRequest = (order) => {
    showConfirmation(order, "reject", "return");
  };

  const handleCheckPaymentStatus = async (order) => {
    const orderId = order?.order_id ?? order?.id;
    if (!orderId) return;
    const userId = localStorage.getItem("user_id") || "";
    setCheckingPaymentOrderId(orderId);
    try {
      const url = `${API_ENDPOINTS.Refund_button_click}?user_id=${encodeURIComponent(userId)}&order_id=${encodeURIComponent(orderId)}`;
      const response = await apiService.get(url);
      const message = response?.message ?? response?.msg ?? "";
      if (response?.success) {
        await fetchProductsForSelectedTab();
        toast.showSuccess(message || "Payment status checked successfully.");
      } else {
        toast.showError(message || "Check payment status failed.");
      }
    } catch (error) {
      console.error("Check payment status error:", error);
      const errMsg = error?.response?.data?.message ?? error?.message ?? "Check payment status failed.";
      toast.showError(errMsg);
    } finally {
      setCheckingPaymentOrderId(null);
    }
  };

  const handleDownloadZip = async (mode) => {
    try {
      const ids = selectedProducts.length > 0 ? selectedProducts : products.map((p) => p.id);
      const query = new URLSearchParams({
        order_ids: ids.join(","),
        mode: mode || "both",
      }).toString();
      const filename = `orders_return_cancel_${new Date().toISOString().slice(0, 10)}.zip`;
      await apiService.downloadFile(`${API_ENDPOINTS.GET_generate_zip_file}?${query}`, filename);
    } catch (error) {
      console.error("ZIP download error:", error);
      alert(error?.message || "Failed to download ZIP.");
    }
  };

  // Handle filter clear based on active tab
  const handleClearFilters = () => {
    if (!isCompanyUser) {
      setSelectedCompany("all");
    }
    if (statusFilter === "Cancelled") {
      setCancelSelectedPaymentStatus("");
      setCancelSelectedOrderStatus("");
      setCancelSelectedRequestStatus("");
    } else if (statusFilter === "Returned") {
      setReturnSelectedPaymentStatus("");
      setReturnSelectedOrderStatus("");
      setReturnSelectedRequestStatus("");
    }
    setSearchValue("");
    setSearchTerm("");
    setHasSearched(false);
    setCurrentPage(1);
  };

  // Get current filter values based on active tab
  const getCurrentPaymentStatus = () => {
    return statusFilter === "Cancelled" ? cancelSelectedPaymentStatus : returnSelectedPaymentStatus;
  };

  const getCurrentOrderStatus = () => {
    return statusFilter === "Cancelled" ? cancelSelectedOrderStatus : returnSelectedOrderStatus;
  };

  const getCurrentRequestStatus = () => {
    return statusFilter === "Cancelled" ? cancelSelectedRequestStatus : returnSelectedRequestStatus;
  };

  // Handle payment status change based on active tab
  const handlePaymentStatusChange = (value) => {
    if (statusFilter === "Cancelled") {
      setCancelSelectedPaymentStatus(value);
    } else if (statusFilter === "Returned") {
      setReturnSelectedPaymentStatus(value);
    }
  };

  // Handle order status change based on active tab
  const handleOrderStatusChange = (value) => {
    if (statusFilter === "Cancelled") {
      setCancelSelectedOrderStatus(value);
    } else if (statusFilter === "Returned") {
      setReturnSelectedOrderStatus(value);
    }
  };

  // Handle request status change based on active tab
  const handleRequestStatusChange = (value) => {
    if (statusFilter === "Cancelled") {
      setCancelSelectedRequestStatus(value);
    } else if (statusFilter === "Returned") {
      setReturnSelectedRequestStatus(value);
    }
  };

  // Check if any filters are active for the current tab
  const hasActiveFilters = () => {
    if (statusFilter === "Cancelled") {
      return selectedCompany !== "all" || cancelSelectedPaymentStatus || cancelSelectedOrderStatus || cancelSelectedRequestStatus || searchTerm;
    } else {
      return selectedCompany !== "all" || returnSelectedPaymentStatus || returnSelectedOrderStatus || returnSelectedRequestStatus || searchTerm;
    }
  };

  // Handle search
  const handleSearch = () => {
    if (!searchValue.trim()) return;
    setSearchTerm(searchValue);
    setCurrentPage(1);
    setHasSearched(true);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchValue("");
    setSearchTerm("");
    setCurrentPage(1);
    setHasSearched(false);
  };

  // Handle Enter key press
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && searchValue.trim()) {
      handleSearch();
    }
  };
   
  // Auto-refresh when search input is cleared
  useEffect(() => {
    if (searchValue === "" && searchTerm !== "") {
      handleClearSearch();
    }
  }, [searchValue]);

  return (
    <Box pt={1.5} pb={3}>

      {/* Compact top controls: tabs + refresh */}
      <TabContainer>
        <Card sx={{ p: 1, borderRadius: 1.5, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
          <FlexBetween gap={1} flexWrap="wrap">
            <Box sx={{ flex: 1, minWidth: { xs: "100%", md: 0 } }}>
              <StyledTabs
                value={statusFilter}
                onChange={handleStatusChange}
                variant="fullWidth"
                scrollButtons={false}
              >
                {TABS.map((tab) => (
                  <StyledTab
                    key={tab.value}
                    value={tab.value}
                    label={
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <span>{tab.value === "Cancelled" ? "❌" : "↩️"}</span>
                          <span>{tab.label}</span>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: (currentTheme) => getStatusColor(currentTheme, tab.category),
                            lineHeight: 1,
                          }}
                        >
                          {counts[tab.value] ?? 0}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </StyledTabs>
            </Box>

            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                minWidth: { xs: "100%", sm: 120 },
              }}
            >
              Refresh
            </Button>
          </FlexBetween>
        </Card>
      </TabContainer>

      {/* Compact filter panel with progressive disclosure */}
      <FilterCard>
        <FilterHeader>
          <FlexBetween gap={1} flexWrap="wrap">
            <Typography variant="subtitle2" fontWeight={700} color="primary">
              Filter & Search
            </Typography>
            <FlexBox alignItems="center" gap={1} flexWrap="wrap">
              {selectedProducts.length > 0 && (
                <Typography variant="body2" color="primary" fontWeight={600}>
                  {selectedProducts.length} selected
                </Typography>
              )}
              {hasActiveFilters() && (
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  size="small"
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Clear All
                </Button>
              )}
            </FlexBox>
          </FlexBetween>
        </FilterHeader>
        <Box sx={{ px: 1.5, py: 1.25 }}>
          {/* One-row desktop filters: Search + Request + Payment + Order + Company */}
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={4}>
              <FlexBox alignItems="center" gap={1}>
                <SearchInput
                  placeholder="Search by Order ID,Customer,Mobile No....."
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    if (hasSearched && e.target.value !== searchTerm) {
                      setHasSearched(false);
                    }
                  }}
                  value={searchValue}
                  onKeyPress={handleKeyPress}
                  sx={{
                    width: "100%",
                    border: "1px solid",
                    borderColor: "grey.300",
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: "background.paper",
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={handleSearch}
                  disabled={!searchValue.trim() || (searchValue === searchTerm && hasSearched)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    height: 40,
                    minWidth: 96,
                    whiteSpace: "nowrap",
                  }}
                >
                  Search
                </Button>
              </FlexBox>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Request Status</InputLabel>
                <Select
                  value={getCurrentRequestStatus()}
                  onChange={(e) => handleRequestStatusChange(e.target.value)}
                  label="Request Status"
                >
                  <MenuItem value="">All</MenuItem>
                  {requestStatusOptions.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={getCurrentPaymentStatus()}
                  onChange={(e) => handlePaymentStatusChange(e.target.value)}
                  label="Payment Status"
                >
                  <MenuItem value="">All</MenuItem>
                  {paymentStatusOptions.map((status) => (
                    <MenuItem key={status.id} value={status.name}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Order Status</InputLabel>
                <Select
                  value={getCurrentOrderStatus()}
                  onChange={(e) => handleOrderStatusChange(e.target.value)}
                  label="Order Status"
                >
                  <MenuItem value="">All</MenuItem>
                  {orderStatusOptions.map((status) => (
                    <MenuItem key={status.id} value={status.name}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Company</InputLabel>
                <Select
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  label="Company"
                  disabled={isCompanyUser}
                >
                  {!isCompanyUser && (
                    <MenuItem value="all">
                      <Chip label="All Companies" size="small" sx={{ mr: 1 }} />
                      All Companies
                    </MenuItem>
                  )}
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      <Chip label={company.name} size="small" color="secondary" variant="outlined" sx={{ mr: 1 }} />
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={1.5} alignItems="center" sx={{ mt: 0.25 }}>
            <Grid item md={10} sx={{ display: { xs: "none", md: "block" } }} />
            <Grid item xs={6} sm={3} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Rows</InputLabel>
                <Select
                  value={pageSize}
                  label="Rows"
                  onChange={handlePageSizeChange}
                >
                  {pageSizeOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </FilterCard>

      {/* Advanced Export Button */}
      {/* <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<GetApp />}
          onClick={() => setAdvancedExportModal(true)}
          disabled={!shouldEnableButton('advanced_export')}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            opacity: !shouldEnableButton('advanced_export') ? 0.6 : 1
          }}
        >
          Advanced Export        </Button>
      </Box> */}

      {/* Table - dedicated Return/Cancel table (no ProductList) */}
      <Card sx={{ marginTop: 1.25 }}>
        <ReturnCancelRequestTable
          data={products}
          searchQuery={searchTerm}
          isLoading={isLoading}
          selectedProducts={selectedProducts}
          onProductSelect={handleProductSelect}
          onSelectAll={handleSelectAll}
          isAllSelected={isAllSelected}
          showCancelRequestActions={statusFilter === "Cancelled"}
          onApproveCancelRequest={handleApproveCancelRequest}
          onRejectCancelRequest={handleRejectCancelRequest}
          updatingCancelRequestOrderId={updatingOrderId}
          showReturnRequestActions={statusFilter === "Returned"}
          onApproveReturnRequest={handleApproveReturnRequest}
          onRejectReturnRequest={handleRejectReturnRequest}
          updatingReturnRequestOrderId={updatingReturnOrderId}
          onCheckPaymentStatus={handleCheckPaymentStatus}
          checkingPaymentOrderId={checkingPaymentOrderId}
          currentPage={currentPage}
          pageSize={pageSize}
          shouldEnableButton={shouldEnableButton}
        />
      </Card>

      {/* Pagination and Showing Info */}
      <Box
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          rowGap: 1,
          flexDirection: "column",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {totalItems > 0
            ? `Showing ${hasSearched ? "all" : (currentPage - 1) * pageSize + 1} ${hasSearched ? "" : "- " + Math.min(currentPage * pageSize, totalItems)} of ${totalItems} requests`
            : `Showing 0 of 0 requests`
          }
        </Typography>

        {!hasSearched && totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <AppPagination
              shape="rounded"
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Box>

      <AdvancedExportModal
        open={advancedExportModal}
        onClose={() => setAdvancedExportModal(false)}
        onRefresh={handleRefresh}
      />

      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={() => setConfirmationDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmButtonText={confirmationDialog.confirmButtonText}
        confirmButtonColor={confirmationDialog.confirmButtonColor}
        isLoading={updatingOrderId !== null || updatingReturnOrderId !== null}
      />
    </Box>
  );
};

export default ReturnCancelRequest;